import type { I18nProvider } from '@refinedev/core';
import { MRT_ColumnFiltersState } from 'mantine-react-table';

export enum ClaimQueryStatusGroup {
  all = 'all',
  created = 'created',
  submitted = 'submitted',
  pending = 'pending',
  completed = 'completed',
  failed = 'failed',
}

export type ClaimQueryStatusGroupCounts = Record<ClaimQueryStatusGroup, number>;

export const claimQueryStatusGroups: Record<ClaimQueryStatusGroup, string[]> = {
  [ClaimQueryStatusGroup.all]: [
    'CREATED',
    'SUBMITTED',
    'PENDING',
    'COMPLETED',
    'FAILED',
  ],
  [ClaimQueryStatusGroup.created]: ['CREATED'],
  [ClaimQueryStatusGroup.submitted]: ['SUBMITTED'],
  [ClaimQueryStatusGroup.pending]: ['PENDING'],
  [ClaimQueryStatusGroup.completed]: ['COMPLETED'],
  [ClaimQueryStatusGroup.failed]: ['FAILED'],
};

export const isClaimQueryStatusesCountResult = (
  value: unknown
): value is Record<string, number> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const getClaimQueryStatusGroupCounts = (
  statusCounts?: Record<string, number> | null
): ClaimQueryStatusGroupCounts =>
  Object.fromEntries(
    (Object.values(ClaimQueryStatusGroup) as ClaimQueryStatusGroup[]).map(
      (group) => [
        group,
        claimQueryStatusGroups[group].reduce(
          (sum, statusKey) => sum + (statusCounts?.[statusKey] ?? 0),
          0
        ),
      ]
    )
  ) as ClaimQueryStatusGroupCounts;

const uniqueSorted = (values: string[]) => Array.from(new Set(values)).sort();

const statusSetsEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
};

export const getClaimQueryStatusFilterValues = (
  columnFilters: MRT_ColumnFiltersState
): string[] =>
  uniqueSorted(
    (columnFilters ?? [])
      .filter((filter) => filter.id === 'status')
      .flatMap((filter) =>
        Array.isArray(filter.value)
          ? filter.value.map(String)
          : [String(filter.value)]
      )
      .filter(Boolean)
  );

export const getActiveClaimQueryStatusGroup = (
  columnFilters: MRT_ColumnFiltersState
): ClaimQueryStatusGroup | null => {
  const selected = getClaimQueryStatusFilterValues(columnFilters);
  if (!selected.length) return ClaimQueryStatusGroup.all;

  for (const group of Object.values(ClaimQueryStatusGroup)) {
    if (statusSetsEqual(selected, uniqueSorted(claimQueryStatusGroups[group]))) {
      return group;
    }
  }

  return null;
};

export const applyClaimQueryStatusGroup = (
  currentFilters: MRT_ColumnFiltersState,
  group: ClaimQueryStatusGroup
): MRT_ColumnFiltersState => {
  const nonStatusFilters = (currentFilters ?? []).filter(
    (filter) => filter.id !== 'status'
  );

  if (group === ClaimQueryStatusGroup.all) {
    return nonStatusFilters;
  }

  return [
    ...nonStatusFilters,
    {
      id: 'status',
      value: claimQueryStatusGroups[group][0],
    },
  ];
};

export const formatClaimQueryStatusGroupLabel = (
  group: ClaimQueryStatusGroup,
  translate?: I18nProvider['translate']
) => {
  const fallbackLabels: Record<ClaimQueryStatusGroup, string> = {
    [ClaimQueryStatusGroup.all]: 'All',
    [ClaimQueryStatusGroup.created]: 'Created',
    [ClaimQueryStatusGroup.submitted]: 'Submitted',
    [ClaimQueryStatusGroup.pending]: 'Pending',
    [ClaimQueryStatusGroup.completed]: 'Completed',
    [ClaimQueryStatusGroup.failed]: 'Failed',
  };

  if (!translate) return fallbackLabels[group];

  return translate(
    `pages:queries.quickFilters.${group}`,
    fallbackLabels[group]
  );
};
